import { useCallback, useEffect, useState } from 'react';
import { api } from '@onvice/portal-shell-api';
import { SettingsSection } from '../edit/SettingsSection';
import PersonMultiPicker, { type PickablePerson } from '../components/PersonMultiPicker';
import { useDialogs } from '../dialogs/useDialogs';

interface ActionDef { action: string; label: string; help?: string }
interface DocTypeDef { docType: string; label: string; actions: ActionDef[] }
interface TaskRuleDef { action: string; label: string; help?: string; owner: boolean; helper: boolean }
interface Catalog { moduleSlug: string; docTypes: DocTypeDef[]; taskRules: TaskRuleDef[] }
interface GrantsResponse {
  enabled: boolean;
  canEnable: boolean;
  cells: Array<{ docType: string; action: string; userIds: string[] }>;
  taskRules: Array<{ action: string; owner: boolean; helper: boolean }>;
  persons: Array<{ id: string; displayName: string; role: string; email?: string | null }>;
}
interface PreviewRow { userId: string; displayName: string; loses: string[]; gains: string[] }

/** Formularzustand des Abschnitts — drei Felder, damit `changed` sagt, was gespeichert werden muss. */
interface AclForm {
  enabled: boolean;
  /** `docType.action` → Personen */
  cells: Record<string, string[]>;
  /** Block A: Aufgaben-Aktion → Häkchen */
  taskRules: Record<string, { owner: boolean; helper: boolean }>;
}

interface Props {
  /** z. B. `/modules/bauberichteV2` — Basis des Modul-Proxys. */
  apiBase: string;
  title?: string;
  description?: string;
  /** Anker-Präfix (`module-acl`), Zellen bekommen `module-acl-cell-<docType>-<action>`. */
  anchor?: string;
}

const cellKey = (d: string, a: string) => `${d}.${a}`;

/**
 * Verwaltung der alternativen Modul-ACL (Plan Jörg 2026-10-13/14): Schalter, Block A
 * (Aufgabenbezug: Eigentümer/Hilfskraft je Aktion) und Block B (Matrix Dokumentart × Funktion →
 * Personen). Lesen/Bearbeiten/Speichern/Abbrechen über `SettingsSection`. Vor dem Einschalten
 * Vorschau „wer verliert was“ und Übernahme der heutigen Rechte (E9).
 */
export function ModuleAclSection({ apiBase, title = 'Modul-ACL', description, anchor = 'module-acl' }: Props) {
  const dlg = useDialogs();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [persons, setPersons] = useState<PickablePerson[]>([]);
  const [canEnable, setCanEnable] = useState(false);
  const [baseline, setBaseline] = useState<AclForm | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewRow[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, g] = await Promise.all([
        api.get<{ success: boolean; data: Catalog }>(`${apiBase}/acl/catalog`),
        api.get<{ success: boolean; data: GrantsResponse }>(`${apiBase}/acl/grants`),
      ]);
      const cells: Record<string, string[]> = {};
      for (const cell of g.data.cells) cells[cellKey(cell.docType, cell.action)] = [...cell.userIds].sort();
      const taskRules: Record<string, { owner: boolean; helper: boolean }> = {};
      for (const r of g.data.taskRules) taskRules[r.action] = { owner: r.owner, helper: r.helper };
      setCatalog(c.data);
      setPersons(g.data.persons.map((p) => ({ id: p.id, displayName: p.displayName, email: p.email ?? null })));
      setCanEnable(g.data.canEnable);
      setBaseline({ enabled: g.data.enabled, cells, taskRules });
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message || 'Modul-ACL nicht ladbar');
    }
  }, [apiBase]);

  useEffect(() => { void load(); }, [load]);

  async function onSave(value: AclForm, changed: Array<keyof AclForm>): Promise<AclForm | void> {
    if (!catalog) return;
    if (changed.includes('cells') || changed.includes('taskRules')) {
      const cells = catalog.docTypes.flatMap((d) => d.actions.map((a) => ({ docType: d.docType, action: a.action, userIds: value.cells[cellKey(d.docType, a.action)] ?? [] })));
      const taskRules = catalog.taskRules.map((r) => ({ action: r.action, owner: value.taskRules[r.action]?.owner ?? r.owner, helper: value.taskRules[r.action]?.helper ?? r.helper }));
      await api.put(`${apiBase}/acl/grants`, { cells, taskRules });
    }
    if (changed.includes('enabled')) {
      await api.put(`${apiBase}/acl/enabled`, { enabled: value.enabled });
    }
    await load();
  }

  async function showPreview() {
    setBusy(true);
    try {
      const r = await api.get<{ success: boolean; data: PreviewRow[] }>(`${apiBase}/acl/preview`);
      setPreview(r.data);
    } catch (e) {
      void dlg.error('Vorschau nicht berechenbar', e);
    } finally { setBusy(false); }
  }

  async function seed() {
    const ok = await dlg.confirm({
      title: 'Heutige Rechte übernehmen?',
      message: 'Für jede Person wird in jede Zelle eingetragen, was sie heute darf. Bestehende Einträge bleiben. Danach kann die Matrix angepasst und der Schalter eingeschaltet werden.',
      confirmLabel: 'Übernehmen',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post<{ success: boolean; data: { persons: number; granted: number } }>(`${apiBase}/acl/seed-from-legacy`);
      void dlg.notify({ kind: 'success', title: 'Rechte übernommen', message: `${r.data.granted} Einträge für ${r.data.persons} Personen übernommen.` });
      await load();
      setPreview(null);
    } catch (e) {
      void dlg.error('Übernahme fehlgeschlagen', e);
    } finally { setBusy(false); }
  }

  if (loadError) {
    return (
      <section data-test-anchor={anchor} className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
        Modul-ACL: {loadError} <button type="button" onClick={() => { void load(); }} className="ml-2 underline">Erneut laden</button>
      </section>
    );
  }

  const personName = (id: string) => persons.find((p) => p.id === id)?.displayName ?? id.slice(0, 8);
  const cellLabel = (key: string) => {
    const [d, a] = key.split('.');
    const dt = catalog?.docTypes.find((x) => x.docType === d);
    const ac = dt?.actions.find((x) => x.action === a);
    return `${dt?.label ?? d} · ${ac?.label ?? a}`;
  };

  return (
    <SettingsSection<AclForm>
      id={anchor}
      testAnchor={anchor}
      title={title}
      description={description ?? 'Einfache Rechte je Modul: Ist der Schalter an, gilt für alles jenseits eigener Dokumente nur diese Matrix. Eigene Aufgaben, Berichte und Zeiten darf jeder immer. Geschäftsführung und Administration dürfen immer alles. Ist der Schalter aus, bleibt alles wie bisher.'}
      baseline={baseline}
      onSave={onSave}
      renderRead={(v) => (
        <div className="space-y-5">
          <div className="flex items-start gap-3" data-test-anchor={`${anchor}-enabled`}>
            <span className={`mt-0.5 inline-flex flex-shrink-0 items-center rounded px-2 py-0.5 text-xs font-medium ${v.enabled ? 'bg-brand-500/15 text-brand-300 border border-brand-500/30' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
              {v.enabled ? 'Ein' : 'Aus'}
            </span>
            <span>
              <span className="block text-slate-100">Modul-ACL</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                {v.enabled ? 'Die Matrix unten entscheidet.' : 'Die Matrix ist vorbereitet, entscheidet aber noch nicht — es gilt die bisherige Rechteprüfung.'}
                {!canEnable && ' Der Schalter wird mit der letzten Ausbaustufe freigegeben.'}
              </span>
            </span>
          </div>

          {catalog && (
            <div data-test-anchor={`${anchor}-task-rules`}>
              <h3 className="text-sm font-semibold text-slate-200 mb-2">Aufgabenbezug (gilt an der jeweiligen Aufgabe)</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-500"><th className="text-left font-medium py-1">Aktion</th><th className="font-medium py-1 w-28">Eigentümer</th><th className="font-medium py-1 w-28">Hilfskraft</th></tr></thead>
                <tbody>
                  {catalog.taskRules.map((r) => {
                    const cur = v.taskRules[r.action] ?? { owner: r.owner, helper: r.helper };
                    return (
                      <tr key={r.action} className="border-t border-slate-800" data-test-anchor={`${anchor}-task-${r.action}`}>
                        <td className="py-1.5 pr-2 text-slate-200">{r.label}{r.help && <span className="block text-xs text-slate-500">{r.help}</span>}</td>
                        <td className="py-1.5 text-center text-slate-300">{cur.owner ? 'ja' : '—'}</td>
                        <td className="py-1.5 text-center text-slate-300">{cur.helper ? 'ja' : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {catalog && catalog.docTypes.map((d) => (
            <div key={d.docType} data-test-anchor={`${anchor}-doc-${d.docType}`}>
              <h3 className="text-sm font-semibold text-slate-200 mb-2">{d.label}</h3>
              <dl className="space-y-2">
                {d.actions.map((a) => {
                  const ids = v.cells[cellKey(d.docType, a.action)] ?? [];
                  return (
                    <div key={a.action} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3" data-test-anchor={`${anchor}-cell-${d.docType}-${a.action}`}>
                      <dt className="sm:w-56 flex-shrink-0 text-slate-300">{a.label}{a.help && <span className="block text-xs text-slate-500">{a.help}</span>}</dt>
                      <dd className="flex flex-wrap gap-1">
                        {ids.length === 0 && <span className="text-xs text-slate-500">niemand (außer GF/Admin)</span>}
                        {ids.map((id) => <span key={id} className="inline-flex items-center px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-200 text-xs">{personName(id)}</span>)}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          ))}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={() => { void showPreview(); }} disabled={busy} data-test-anchor={`${anchor}-preview`} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-700 text-slate-200 text-sm hover:bg-slate-800 disabled:opacity-50 min-h-[44px]">
              Vorschau: wer verliert was
            </button>
            <button type="button" onClick={() => { void seed(); }} disabled={busy} data-test-anchor={`${anchor}-seed`} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-700 text-slate-200 text-sm hover:bg-slate-800 disabled:opacity-50 min-h-[44px]">
              Heutige Rechte übernehmen
            </button>
          </div>

          {preview && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm" data-test-anchor={`${anchor}-preview-result`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-amber-200">Beim Einschalten mit der heutigen Matrix</span>
                <button type="button" onClick={() => setPreview(null)} className="text-xs text-slate-400 underline">schließen</button>
              </div>
              {preview.every((r) => r.loses.length === 0 && r.gains.length === 0) && <p className="text-slate-300 mt-1">Niemand verliert oder gewinnt etwas.</p>}
              <ul className="mt-2 space-y-1">
                {preview.filter((r) => r.loses.length > 0 || r.gains.length > 0).map((r) => (
                  <li key={r.userId} className="text-slate-200">
                    <span className="font-medium">{r.displayName}</span>
                    {r.loses.length > 0 && <span className="block text-xs text-rose-300">verliert: {r.loses.map(cellLabel).join(', ')}</span>}
                    {r.gains.length > 0 && <span className="block text-xs text-emerald-300">gewinnt: {r.gains.map(cellLabel).join(', ')}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      renderEdit={(v, setField) => (
        <div className="space-y-5">
          <label className={`flex items-start gap-3 ${canEnable ? 'cursor-pointer' : 'opacity-60'}`} data-test-anchor={`${anchor}-enabled`}>
            <input type="checkbox" checked={v.enabled} disabled={!canEnable} onChange={(e) => setField('enabled', e.target.checked)} className="mt-1 h-5 w-5 rounded border-slate-600 bg-slate-800" />
            <span>
              <span className="block text-slate-100">Modul-ACL einschalten</span>
              <span className="block text-xs text-slate-500 mt-0.5">Vorher „Heutige Rechte übernehmen“ ausführen, sonst dürfte außer GF und Admin niemand etwas.{!canEnable && ' Der Schalter wird mit der letzten Ausbaustufe freigegeben.'}</span>
            </span>
          </label>

          {catalog && (
            <div data-test-anchor={`${anchor}-task-rules`}>
              <h3 className="text-sm font-semibold text-slate-200 mb-2">Aufgabenbezug (gilt an der jeweiligen Aufgabe)</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-500"><th className="text-left font-medium py-1">Aktion</th><th className="font-medium py-1 w-28">Eigentümer</th><th className="font-medium py-1 w-28">Hilfskraft</th></tr></thead>
                <tbody>
                  {catalog.taskRules.map((r) => {
                    const cur = v.taskRules[r.action] ?? { owner: r.owner, helper: r.helper };
                    const set = (patch: Partial<{ owner: boolean; helper: boolean }>) => setField('taskRules', { ...v.taskRules, [r.action]: { ...cur, ...patch } });
                    return (
                      <tr key={r.action} className="border-t border-slate-800" data-test-anchor={`${anchor}-task-${r.action}`}>
                        <td className="py-1.5 pr-2 text-slate-200">{r.label}{r.help && <span className="block text-xs text-slate-500">{r.help}</span>}</td>
                        <td className="py-1.5 text-center"><input type="checkbox" checked={cur.owner} onChange={(e) => set({ owner: e.target.checked })} className="h-5 w-5 rounded border-slate-600 bg-slate-800" aria-label={`${r.label} — Eigentümer`} /></td>
                        <td className="py-1.5 text-center"><input type="checkbox" checked={cur.helper} onChange={(e) => set({ helper: e.target.checked })} className="h-5 w-5 rounded border-slate-600 bg-slate-800" aria-label={`${r.label} — Hilfskraft`} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {catalog && catalog.docTypes.map((d) => (
            <div key={d.docType} data-test-anchor={`${anchor}-doc-${d.docType}`}>
              <h3 className="text-sm font-semibold text-slate-200 mb-2">{d.label}</h3>
              <div className="space-y-3">
                {d.actions.map((a) => {
                  const key = cellKey(d.docType, a.action);
                  return (
                    <div key={a.action} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3" data-test-anchor={`${anchor}-cell-${d.docType}-${a.action}`}>
                      <div className="sm:w-56 flex-shrink-0 text-slate-300 pt-2">{a.label}{a.help && <span className="block text-xs text-slate-500">{a.help}</span>}</div>
                      <div className="flex-1 min-w-0">
                        <PersonMultiPicker
                          value={v.cells[key] ?? []}
                          onChange={(next) => setField('cells', { ...v.cells, [key]: [...next].sort() })}
                          persons={persons}
                          testAnchor={`${anchor}-picker-${d.docType}-${a.action}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    />
  );
}
